import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutGrid, MousePointer2, RefreshCcw, Save } from "lucide-react";
import toast from "react-hot-toast";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Row } from "../../../Components/ui";
import {
  assignReservationOperationalSeat,
  clearReservationOperationalSeat,
  createPackageOperationalSeat,
} from "../../../api";
import {
  FreeSeatPackage,
  FreeSeatBoard,
  FreeSeatBoardReservation,
  FreeSeatBoardSeat,
  freeSeatAssignmentService,
} from "../../../api/services/free-seat-assignment.service";
import LayoutNodeCanvas, { LayoutNodeCanvasNode } from "../../Layouts/core/LayoutNodeCanvas";

type PageMode = "assign" | "layout";
type ReservationSortKey = "name" | "phone";
type AutoArrangeOptionValue =
  | "columns-3"
  | "columns-4"
  | "columns-5"
  | "columns-6"
  | "rows-3"
  | "rows-4"
  | "rows-5"
  | "rows-6";

const AUTO_ARRANGE_OPTIONS: Array<{
  value: AutoArrangeOptionValue;
  label: string;
  columns?: number;
  rows?: number;
}> = [
  { value: "columns-3", label: "3 x n", columns: 3 },
  { value: "columns-4", label: "4 x n", columns: 4 },
  { value: "columns-5", label: "5 x n", columns: 5 },
  { value: "columns-6", label: "6 x n", columns: 6 },
  { value: "rows-3", label: "n x 3", rows: 3 },
  { value: "rows-4", label: "n x 4", rows: 4 },
  { value: "rows-5", label: "n x 5", rows: 5 },
  { value: "rows-6", label: "n x 6", rows: 6 },
];

const todayString = () => {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

const isFreeSeatPackage = (item: FreeSeatPackage) => item.has_spots === false;

const toDraftSeats = (board?: FreeSeatBoard | null): FreeSeatBoardSeat[] =>
  (board?.seats || []).map((seat) => ({ ...seat }));

const getSeatFill = (seat: FreeSeatBoardSeat) => {
  if (!seat.is_active) return "#94A3B8";
  if (seat.assigned_reservation) return "#059669";
  return "#2563EB";
};

const toCanvasNodes = (seats: FreeSeatBoardSeat[]): LayoutNodeCanvasNode[] =>
  seats.map((seat) => ({
    id: seat.id,
    label: seat.name,
    x: seat.x,
    y: seat.y,
    width: seat.width,
    height: seat.height,
    rotation: seat.rotation,
    is_active: seat.is_active,
    meta: seat.meta,
    fill: getSeatFill(seat),
  }));

const formatReservationSummary = (reservation: { customer_name: string; phone: string }) =>
  `${reservation.customer_name || "-"} ${reservation.phone || ""}`.trim();

const compareReservationText = (first: string, second: string) =>
  (first || "").localeCompare(second || "", "ko-KR", {
    numeric: true,
    sensitivity: "base",
  });

const compareReservations = (
  first: FreeSeatBoardReservation,
  second: FreeSeatBoardReservation,
  sortKey: ReservationSortKey,
) => {
  const primary =
    sortKey === "phone"
      ? compareReservationText(first.phone, second.phone)
      : compareReservationText(first.customer_name, second.customer_name);
  if (primary !== 0) return primary;

  const secondary =
    sortKey === "phone"
      ? compareReservationText(first.customer_name, second.customer_name)
      : compareReservationText(first.phone, second.phone);
  if (secondary !== 0) return secondary;

  return first.id - second.id;
};

export default function FreeSeatAssignmentPage() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = React.useState(todayString());
  const [selectedPackageId, setSelectedPackageId] = React.useState<number | null>(null);
  const [selectedSeatId, setSelectedSeatId] = React.useState<number | null>(null);
  const [mode, setMode] = React.useState<PageMode>("assign");
  const [draftSeats, setDraftSeats] = React.useState<FreeSeatBoardSeat[]>([]);
  const [autoArrangeOption, setAutoArrangeOption] = React.useState<AutoArrangeOptionValue>("columns-4");
  const [reservationSortKey, setReservationSortKey] = React.useState<ReservationSortKey>("name");

  const packagesQuery = useQuery({
    queryKey: ["free-seat-assignment-packages"],
    queryFn: freeSeatAssignmentService.listFreeSeatPackages,
    staleTime: 1000 * 60,
  });

  const freeSeatPackages = React.useMemo(
    () => (packagesQuery.data || []).filter(isFreeSeatPackage),
    [packagesQuery.data],
  );

  React.useEffect(() => {
    if (!freeSeatPackages.length) {
      if (selectedPackageId !== null) {
        setSelectedPackageId(null);
      }
      return;
    }

    if (!selectedPackageId || !freeSeatPackages.some((item) => item.id === selectedPackageId)) {
      setSelectedPackageId(freeSeatPackages[0].id);
    }
  }, [freeSeatPackages, selectedPackageId]);

  const selectedPackage = React.useMemo(
    () => freeSeatPackages.find((item) => item.id === selectedPackageId) || null,
    [freeSeatPackages, selectedPackageId],
  );

  const boardQuery = useQuery({
    queryKey: ["free-seat-assignment-board", selectedPackageId, selectedDate],
    queryFn: () => freeSeatAssignmentService.getBoard(selectedPackageId as number, selectedDate),
    enabled: Boolean(selectedPackageId && selectedDate),
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    setDraftSeats(toDraftSeats(boardQuery.data));
    setSelectedSeatId(null);
  }, [boardQuery.data]);

  const invalidateBoard = () =>
    queryClient.invalidateQueries({
      queryKey: ["free-seat-assignment-board", selectedPackageId, selectedDate],
    });

  const assignMutation = useMutation({
    mutationFn: ({ reservationId, seatId }: { reservationId: number; seatId: number }) =>
      assignReservationOperationalSeat(reservationId, seatId),
    onSuccess: () => {
      toast.success("운영 자리를 배정했습니다.");
      invalidateBoard();
    },
    onError: () => toast.error("운영 자리 배정에 실패했습니다."),
  });

  const clearMutation = useMutation({
    mutationFn: (reservationId: number) => clearReservationOperationalSeat(reservationId),
    onSuccess: () => {
      toast.success("운영 자리 배정을 해제했습니다.");
      invalidateBoard();
    },
    onError: () => toast.error("운영 자리 배정 해제에 실패했습니다."),
  });

  const saveLayoutMutation = useMutation({
    mutationFn: () => {
      if (!boardQuery.data || !selectedPackageId) {
        throw new Error("저장할 자유석 배치를 찾을 수 없습니다.");
      }
      return freeSeatAssignmentService.saveLayout({
        package: selectedPackageId,
        layout: {
          canvas_width: boardQuery.data.layout.canvas_width,
          canvas_height: boardQuery.data.layout.canvas_height,
          background_image_url: boardQuery.data.layout.background_image_url,
        },
        seats: draftSeats.map((seat, index) => ({
          id: seat.id,
          x: seat.x,
          y: seat.y,
          width: seat.width,
          height: seat.height,
          rotation: seat.rotation,
          display_order: seat.display_order || index + 1,
          meta: seat.meta,
        })),
      });
    },
    onSuccess: () => {
      toast.success("자유석 배치를 저장했습니다.");
      invalidateBoard();
    },
    onError: () => toast.error("자유석 배치 저장에 실패했습니다."),
  });

  const createMissingMutation = useMutation({
    mutationFn: async () => {
      if (!boardQuery.data || !selectedPackageId) {
        return;
      }
      const missingCount = Math.max((boardQuery.data.package.capacity || 0) - boardQuery.data.seats.length, 0);
      for (let index = 0; index < missingCount; index += 1) {
        const displayOrder = boardQuery.data.seats.length + index + 1;
        await createPackageOperationalSeat({
          package: selectedPackageId,
          name: String(displayOrder),
          display_order: displayOrder,
          is_active: true,
        });
      }
    },
    onSuccess: () => {
      toast.success("부족한 운영 자리를 생성했습니다.");
      invalidateBoard();
    },
    onError: () => toast.error("운영 자리 생성에 실패했습니다."),
  });

  const board = boardQuery.data;
  const selectedSeat = draftSeats.find((seat) => seat.id === selectedSeatId) || null;
  const selectedBoardSeat = board?.seats.find((seat) => seat.id === selectedSeatId) || null;
  const selectedAssignment = selectedBoardSeat?.assigned_reservation || null;
  const unassignedReservations = React.useMemo(
    () =>
      (board?.reservations || [])
        .filter((reservation) => !reservation.assigned_seat)
        .sort((first, second) => compareReservations(first, second, reservationSortKey)),
    [board?.reservations, reservationSortKey],
  );
  const assignedReservations = board?.reservations.filter((reservation) => reservation.assigned_seat) || [];
  const missingSeatCount = Math.max((board?.package.capacity || 0) - (board?.seats.length || 0), 0);
  const canAssignToSelectedSeat = Boolean(selectedSeat && selectedSeat.is_active && !selectedAssignment);

  const handleAssign = (reservationId: number) => {
    if (!selectedSeat || !canAssignToSelectedSeat) {
      toast.error("배정할 빈 운영 자리를 선택해 주세요.");
      return;
    }
    assignMutation.mutate({ reservationId, seatId: selectedSeat.id });
  };

  const handleAutoArrange = () => {
    setDraftSeats((prev) => {
      const selectedOption = AUTO_ARRANGE_OPTIONS.find((option) => option.value === autoArrangeOption);
      const columns = selectedOption?.columns || Math.max(1, Math.ceil((prev.length || 1) / (selectedOption?.rows || 1)));
      return prev.map((seat, index) => ({
        ...seat,
        x: 64 + (index % columns) * 88,
        y: 72 + Math.floor(index / columns) * 88,
        display_order: index + 1,
      }));
    });
  };

  const handleMoveSeat = (seatId: number, position: { x: number; y: number }) => {
    setDraftSeats((prev) =>
      prev.map((seat) => (seat.id === seatId ? { ...seat, x: position.x, y: position.y } : seat)),
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="자유석 배정" pageTitle="예약" />

        <Card className="mb-3">
          <CardBody>
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Label className="form-label">예약 날짜</Label>
                <Input
                  type="date"
                  aria-label="예약 날짜"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                />
              </Col>
              <Col md={5}>
                <Label className="form-label">예약 상품</Label>
                <Input
                  type="select"
                  aria-label="자유석 패키지"
                  value={selectedPackageId || ""}
                  onChange={(event) => setSelectedPackageId(Number(event.target.value) || null)}
                  disabled={packagesQuery.isLoading}
                >
                  {freeSeatPackages.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} · 자유석
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={12} className="d-flex flex-wrap gap-2">
                <Button
                  type="button"
                  color={mode === "assign" ? "primary" : "light"}
                  onClick={() => setMode("assign")}
                >
                  <MousePointer2 className="me-1" size={16} />
                  배정 모드
                </Button>
                <Button
                  type="button"
                  color={mode === "layout" ? "primary" : "light"}
                  onClick={() => setMode("layout")}
                >
                  <LayoutGrid className="me-1" size={16} />
                  배치 편집
                </Button>
                <Button type="button" color="light" onClick={() => boardQuery.refetch()} disabled={boardQuery.isFetching}>
                  <RefreshCcw className="me-1" size={16} />
                  새로고침
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {!packagesQuery.isLoading && !freeSeatPackages.length ? (
          <Card>
            <CardBody className="text-muted">등록된 자유석 예약 상품이 없습니다.</CardBody>
          </Card>
        ) : null}

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
          <div className="min-w-0">
            <Card className="h-100">
              <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <h5 className="mb-1">{selectedPackage?.name || "자유석 배치도"}</h5>
                  <div className="d-flex flex-wrap gap-2 small text-muted">
                    <span>예약 {board?.reservations.length || 0}건</span>
                    <span>운영 자리 {board?.seats.length || 0}개</span>
                    <span>좌석 수 {board?.package.capacity || "-"}</span>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <Input
                    type="select"
                    aria-label="자동 정렬 옵션"
                    value={autoArrangeOption}
                    onChange={(event) => setAutoArrangeOption(event.target.value as AutoArrangeOptionValue)}
                    disabled={mode !== "layout"}
                    className="w-auto"
                  >
                    {AUTO_ARRANGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                  <Button type="button" color="light" onClick={handleAutoArrange} disabled={mode !== "layout" || !draftSeats.length}>
                    자동 정렬
                  </Button>
                  <Button
                    type="button"
                    color="primary"
                    onClick={() => saveLayoutMutation.mutate()}
                    disabled={mode !== "layout" || saveLayoutMutation.isPending || !draftSeats.length}
                  >
                    <Save className="me-1" size={16} />
                    배치 저장
                  </Button>
                  {missingSeatCount > 0 ? (
                    <Button
                      type="button"
                      color="secondary"
                      onClick={() => createMissingMutation.mutate()}
                      disabled={createMissingMutation.isPending}
                    >
                      부족한 자리 생성 ({missingSeatCount}개)
                    </Button>
                  ) : null}
                </div>
              </CardHeader>
              <CardBody className="min-h-[520px]">
                {boardQuery.isLoading ? (
                  <div className="py-5 text-center text-muted">자유석 배정 현황을 불러오는 중입니다.</div>
                ) : board ? (
                  <LayoutNodeCanvas
                    canvasWidth={board.layout.canvas_width}
                    canvasHeight={board.layout.canvas_height}
                    backgroundImageUrl={board.layout.background_image_url}
                    nodes={toCanvasNodes(draftSeats)}
                    selectedNodeId={selectedSeatId}
                    allowDrag={mode === "layout"}
                    onSelect={setSelectedSeatId}
                    onMove={handleMoveSeat}
                  />
                ) : (
                  <div className="py-5 text-center text-muted">날짜와 자유석 상품을 선택해 주세요.</div>
                )}
              </CardBody>
            </Card>
          </div>

          <div className="min-w-0">
            <Card className="h-100">
              <CardHeader>
                <h5 className="mb-0">예약 지정</h5>
              </CardHeader>
              <CardBody>
                <div className="mb-4 rounded border p-3">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div>
                      <div className="fw-semibold">{selectedSeat?.name || "운영 자리 선택"}</div>
                      <div className="small text-muted">
                        {selectedSeat ? (selectedSeat.is_active ? "활성 자리" : "비활성 자리") : "좌측 배치도에서 자리를 선택하세요."}
                      </div>
                    </div>
                    {selectedAssignment ? <Badge color="success">배정됨</Badge> : <Badge color="secondary">미배정</Badge>}
                  </div>
                  {selectedAssignment ? (
                    <div className="mt-3">
                      <div className="small text-muted">현재 예약</div>
                      <div className="fw-semibold">{formatReservationSummary(selectedAssignment)}</div>
                      <Button
                        type="button"
                        color="danger"
                        size="sm"
                        className="mt-2"
                        onClick={() => clearMutation.mutate(selectedAssignment.id)}
                        disabled={clearMutation.isPending}
                      >
                        배정 해제
                      </Button>
                    </div>
                  ) : null}
                </div>

                <div className="mb-4">
                  <div className="mb-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <h6 className="mb-0">미배정 예약</h6>
                    <div className="d-flex align-items-center gap-2">
                      <span className="small text-muted">{unassignedReservations.length}건</span>
                      <Input
                        type="select"
                        aria-label="미배정 예약 정렬"
                        value={reservationSortKey}
                        onChange={(event) => setReservationSortKey(event.target.value as ReservationSortKey)}
                        className="w-auto py-1 text-sm"
                      >
                        <option value="name">이름순</option>
                        <option value="phone">전화번호순</option>
                      </Input>
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {unassignedReservations.length ? (
                      unassignedReservations.map((reservation) => (
                        <div key={reservation.id} className="rounded border p-2">
                          <div className="fw-semibold">{formatReservationSummary(reservation)}</div>
                          <div className="small text-muted">
                            {reservation.state} · {reservation.order_state}
                          </div>
                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleAssign(reservation.id)}
                            disabled={!canAssignToSelectedSeat || assignMutation.isPending}
                          >
                            {reservation.customer_name} 배정
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="rounded border p-3 text-muted">미배정 예약이 없습니다.</div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="mb-2 d-flex align-items-center justify-content-between">
                    <h6 className="mb-0">배정된 예약</h6>
                    <span className="small text-muted">{assignedReservations.length}건</span>
                  </div>
                  <div className="d-flex flex-column gap-2">
                    {assignedReservations.length ? (
                      assignedReservations.map((reservation) => (
                        <div key={reservation.id} className="rounded border p-2">
                          <div className="fw-semibold">{formatReservationSummary(reservation)}</div>
                          <div className="small text-muted">현재 자리 {reservation.assigned_seat?.name || "-"}</div>
                          <Button
                            type="button"
                            color="light"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleAssign(reservation.id)}
                            disabled={!canAssignToSelectedSeat || assignMutation.isPending}
                          >
                            이 자리로 이동
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="rounded border p-3 text-muted">배정된 예약이 없습니다.</div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </Container>
    </div>
  );
}
